import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './FilterDropdown.css';
import { iconMap } from '../NewActivityModal';
import { useForegroundInterval } from '../../services/AppStateService';

const ALL_TYPES = ['workout', 'meal', 'media', 'metric', 'survey'];
const ALL_STATUSES = ['complete', 'pending', 'ongoing', 'expired'];

const DEFAULT_START_DATE = '2010-01-01';
const getTodayDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const titleCase = (s) => {
  if (!s) return '';
  const str = String(s);
  return str.length ? str[0].toUpperCase() + str.slice(1) : str;
};

const areAllSelected = (map, keys) => {
  if (!map) return false;
  return keys.every((k) => !!map[k]);
};

const FilterDropdown = forwardRef(({ onFilterChange, hideTrigger = false, disabled = false, onSummaryChange, videoScope = 'mine', onVideoScopeChange }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef(null);
  const panelRef = useRef(null);
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
  const [endDate, setEndDate] = useState(getTodayDate());
  // Tracks whether the end date is "auto-following today" (the default)
  // vs an explicit user pick. When the calendar rolls over while the
  // app is left open overnight, we silently advance endDate so the
  // dropdown label keeps reading "Displaying all activities" instead
  // of a stale date range.
  const [endDateFollowsToday, setEndDateFollowsToday] = useState(true);

  // Re-check the date once a minute and on tab focus / wake. Cheap
  // enough that we don't need a more elaborate scheduling scheme.
  const syncToday = useCallback(() => {
    const today = getTodayDate();
    setEndDate(prev => (prev === today ? prev : today));
  }, []);

  useEffect(() => {
    if (!endDateFollowsToday) return undefined;
    syncToday();
    const onFocus = () => syncToday();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [endDateFollowsToday, syncToday]);

  // The resume tick replaces what the old visibilitychange handler did here.
  useForegroundInterval(syncToday, endDateFollowsToday ? 60 * 1000 : null);
  const [types, setTypes] = useState(() => ({
    workout: false,
    meal: false,
    media: false,
    metric: false,
    survey: false,
  }));
  const [statuses, setStatuses] = useState(() => ({
    complete: true,
    pending: true,
    ongoing: true,
    expired: true,
  }));

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(panelRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      ) || []);
      if (focusable.length === 0) {
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [isOpen]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      toggleRef.current?.focus();
    }
  }, [isOpen]);

  const isMounted = useRef(false);

  useEffect(() => {
    // Activities already starts with this control's semantic default (the
    // complete feed). Do not replace that first request with an equivalent
    // explicit date-range request while it is still in flight.
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFilterChange({
      startDate,
      endDate,
      types,
      statuses,
    });
  }, [startDate, endDate, types, statuses, onFilterChange]);

  const toggleType = (t) => {
    setTypes((prev) => ({ ...prev, [t]: !prev[t] }));
  };

  const toggleStatus = (s) => {
    setStatuses((prev) => ({ ...prev, [s]: !prev[s] }));
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    setEndDate(value);
    setEndDateFollowsToday(value === getTodayDate());
  };

  const handleClear = () => {
    if (disabled) return;
    setStartDate(DEFAULT_START_DATE);
    setEndDate(getTodayDate());
    setEndDateFollowsToday(true);
    const defaultTypes = { workout: false, meal: false, media: false, metric: false, survey: false };
    const defaultStatuses = { complete: true, pending: true, ongoing: true, expired: true };
    setTypes(defaultTypes);
    setStatuses(defaultStatuses);
    onVideoScopeChange?.('mine');
    setIsOpen(false);
  };

  const getButtonLabel = () => {
    let label = 'Displaying ';

    const selectedTypes = ALL_TYPES.filter((t) => !!(types && types[t]));
    const allTypesOn = areAllSelected(types, ALL_TYPES);
    if (!types || selectedTypes.length === 0 || allTypesOn) {
      label += 'all activities';
    } else {
      const pretty = selectedTypes.map((t) => (t === 'media' ? 'media' : `${t}s`));
      label += pretty.join(', ');
    }

    const selectedStatuses = ALL_STATUSES.filter((s) => !!(statuses && statuses[s]));
    const allStatusesOn = areAllSelected(statuses, ALL_STATUSES);
    if (statuses && selectedStatuses.length > 0 && !allStatusesOn) {
      label += ` (${selectedStatuses.map(titleCase).join(', ')})`;
    }

    const isDefaultDateRange = startDate === DEFAULT_START_DATE && endDate === getTodayDate();

    if (!isDefaultDateRange) {
      if (startDate && endDate) {
          label += ` between ${startDate} and ${endDate}`;
      } else if (startDate) {
          label += ` from ${startDate}`;
      } else if (endDate) {
          label += ` until ${endDate}`;
      }
    }
    
    return label;
  };

  const label = getButtonLabel();
  // Empty and full selections both mean unrestricted in the feed query.
  const isSubset = (map, keys) => {
    const count = keys.filter(key => map[key]).length;
    return count > 0 && count < keys.length;
  };
  const active = isSubset(types, ALL_TYPES) || isSubset(statuses, ALL_STATUSES)
    || Boolean(startDate && startDate !== DEFAULT_START_DATE)
    || Boolean(endDate && endDate !== getTodayDate());
  useEffect(() => {
    onSummaryChange?.({ active, label });
  }, [active, label, onSummaryChange]);

  useImperativeHandle(ref, () => ({
    open: (returnFocusTo) => {
      if (disabled) return;
      if (hideTrigger) toggleRef.current = returnFocusTo;
      setIsOpen(true);
    },
    clear: handleClear,
    // Camera "Videos in Media": show only one activity type (dates/statuses kept).
    showOnlyType: (type, scope = 'mine') => {
      if (disabled || !ALL_TYPES.includes(type)) return;
      setTypes(Object.fromEntries(ALL_TYPES.map(t => [t, t === type])));
      if (type === 'media') onVideoScopeChange?.(scope === 'feed' ? 'feed' : 'mine');
      setIsOpen(false);
    },
  }));

  return (
    <>
      {!hideTrigger && <div className="filter-dropdown-container">
      <button
        ref={toggleRef}
        className="filter-toggle-btn"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {getButtonLabel()} <span className="arrow">{isOpen ? '▲' : '▼'}</span>
      </button>
      </div>}
      {isOpen && createPortal((
        <div
          className="filter-dropdown-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false);
          }}
        >
          <div
            ref={panelRef}
            className="filter-dropdown-content"
            role="dialog"
            aria-modal="true"
            aria-label="Filter activities"
            tabIndex={-1}
          >
            <div className="filter-dialog-header">
              <h2>Filter activities</h2>
              <button
                type="button"
                className="filter-dialog-close"
                aria-label="Close activity filters"
                onClick={() => setIsOpen(false)}
              >
                ×
              </button>
            </div>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="activity-filter-from">From</label>
              <input 
                type="date" 
                disabled={disabled}
                id="activity-filter-from"
                value={startDate} 
                onChange={handleStartDateChange} 
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label htmlFor="activity-filter-to">To</label>
              <input 
                type="date" 
                disabled={disabled}
                id="activity-filter-to"
                value={endDate} 
                onChange={handleEndDateChange} 
                className="filter-input"
              />
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-group full-width">
              <label>Type</label>
              <div className="filter-toggle-grid">
                {ALL_TYPES.map((t) => {
                  const isOn = !!types[t];
                  return (
                    <button
                      key={t}
                      type="button"
                      className={`filter-chip ${isOn ? 'active' : ''}`}
                      disabled={disabled}
                      onClick={() => toggleType(t)}
                      aria-pressed={isOn}
                    >
                      <span className="filter-chip-icon">{iconMap[t] || null}</span>
                      <span className="filter-chip-label">{titleCase(t)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-group full-width">
              <label>Status</label>
              <div className="filter-toggle-grid">
                {ALL_STATUSES.map((s) => {
                  const isOn = !!statuses[s];
                  return (
                    <button
                      key={s}
                      type="button"
                      className={`filter-chip ${isOn ? 'active' : ''}`}
                      disabled={disabled}
                      onClick={() => toggleStatus(s)}
                      aria-pressed={isOn}
                    >
                      <span className="filter-chip-label">{titleCase(s)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {types.media && onVideoScopeChange && <div className="filter-row">
            <div className="filter-group full-width">
              <label htmlFor="activity-video-scope">Videos</label>
              <select id="activity-video-scope" className="filter-input" disabled={disabled} value={videoScope}
                onChange={event => onVideoScopeChange(event.target.value)}>
                <option value="mine">My videos</option>
                <option value="feed">All accessible videos</option>
              </select>
              <p>Includes live and recorded videos. Public means signed-in members; sharing permissions still apply.</p>
            </div>
          </div>}
          <div className="filter-actions">
            <button className="btn-text" disabled={disabled} onClick={handleClear}>Clear Filters</button>
          </div>
          </div>
        </div>
      ), document.body)}
    </>
  );
});

export default FilterDropdown;
